import ReauthModal from 'components/ReauthModal';
import { lazy, Suspense, useEffect, useReducer } from 'react';
import { useSelector } from 'react-redux';
import { selectUser } from 'slices/auth';
import { selectAppReady, selectIframeReady } from 'slices/reauth';
import PanicDetectors from './PanicDetectors';

// Pre-auth shell for the main domain. It renders the lightweight login surface first (the auth
// iframe via ReauthModal) and mounts the heavy main App behind it once the user is authenticated.
// The App chunk is prefetched on mount so it downloads in parallel with the user typing their code.
//
// Keeping App (and Safety) behind a lazy boundary is what keeps the shell's first-paint chunk free
// of MUI / the whole app graph; see vite.config manualChunks + bin/check-bundle-separation.

const App = lazy(() => import('./App'));
const Safety = lazy(() => import('./Safety'));

const Shell = () => {
  const user = useSelector(selectUser);
  const iframeReady = useSelector(selectIframeReady);
  const appReady = useSelector(selectAppReady);

  // On the static mirror the client-side ip.js fills window.ClientInfo asynchronously — the
  // address/country race and the incognito probe resolve after mount — and fires `clientinfochange`
  // as it does. Re-render on that so the pre-auth gate re-reads the (mutated-in-place) ClientInfo
  // instead of sticking on its initial fail-safe state. Harmless with the server ip.js, which sets
  // everything synchronously and never fires the event.
  const [, refreshClientInfo] = useReducer((n) => n + 1, 0);
  useEffect(() => {
    const onChange = () => refreshClientInfo();
    window.addEventListener('clientinfochange', onChange);
    return () => window.removeEventListener('clientinfochange', onChange);
  }, []);

  const clientInfo = window.ClientInfo;

  // Warm the main App bundle once the login iframe is up (gated on iframeReady), in the background,
  // so it is downloaded + module-evaluated by the time login completes. Deliberately NOT eager: the
  // heavy MUI App chunk must not contend with the login bundle's first paint / interactivity. The
  // matching lazy() import dedupes, so mounting <App/> below is then instant.
  useEffect(() => {
    if (!iframeReady) return;
    import('./App');
  }, [iframeReady]);

  // Once the login UI is up, warm the routed page chunks in idle time too, so the first page the
  // app shows doesn't have to lazy-load. Deferred to idle + gated on iframeReady so it never
  // competes with the login iframe's first paint ("init the JS during login, but don't slow login").
  useEffect(() => {
    if (!iframeReady) return;
    let cancelled = false;
    const warm = () => import('./App').then((mod) => !cancelled && mod.prefetchPages?.());
    const ric = window.requestIdleCallback;
    const id = ric ? ric(warm) : setTimeout(warm, 300);
    return () => {
      cancelled = true;
      if (ric && window.cancelIdleCallback) window.cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, [iframeReady]);

  // A native host that owns the session (window.In6to.hosted) is a trusted, hardened environment by
  // construction, so the pre-auth client-safety gate (VPN / incognito / HTTPS) does not apply to it;
  // it also may not expose window.ClientInfo at all. Skip the gate under a host.
  if (!window.In6to?.hosted && !clientInfo?.isSafe()) {
    return (
      <>
        <PanicDetectors />
        <Suspense fallback={null}>
          <Safety
            vpnRequired={clientInfo?.isVpnRequired?.() ?? false}
            incognitoRequired={clientInfo?.isIncognitoRequired?.() ?? true}
            httpsOnlyRequired={clientInfo?.isHttpsOnlyRequired?.() ?? false}
          />
        </Suspense>
      </>
    );
  }

  // The real login screen (ReauthModal + its iframe) is KEPT mounted — not swapped for a
  // placeholder — until the first app page has actually mounted (App.js <Ready> -> reauth.appReady).
  // Once the user is authenticated the App mounts behind it and the backend-chosen page loads;
  // when that page is on screen the login screen is removed (and the login bundle unloaded). The
  // structure below is intentionally stable so the login iframe is NOT torn down/reloaded the
  // moment `user` flips — only when the app is actually ready.
  return (
    <>
      {/* Armed for the whole life of the tab, logged in or not — the login screen needs the panic
          exit as much as an open session does. */}
      <PanicDetectors />
      {user && (
        <Suspense fallback={null}>
          <App />
        </Suspense>
      )}
      {!window.In6to?.hosted && (!user || !appReady) && <ReauthModal show isInitialAuth />}
    </>
  );
};

export default Shell;
